/**
 * Four evidence cards, and the one that is missing.
 *
 * The temptation is a row of four green ticks and a COMPLETED stamp. That
 * picture is the opposite of the claim: `internal/domain/task_state.go` reaches
 * `COMPLETED` only from `REVIEWING` or `APPROVAL_REQUIRED`, and the API has no
 * path that writes a task state directly, so what the product actually
 * guarantees is that the gate **stays shut** when the evidence is not there.
 *
 * So the fourth card is empty, the transition is drawn locked, and the figure
 * says why: nobody defined the command that produces that report. Kimene does
 * not invent one — a made-up test command that passes is worse than no test
 * command at all, because it produces evidence that looks like evidence.
 */
import { Panel, Badge } from '../../chrome'
import { Stagger, Stamp } from '../../kit'
import { useT } from '../../i18n/context'
import { EVIDENCE } from '../../data/transcript'
import './EvidenceChain.css'

const KIND_LABEL: Record<string, string> = {
  diff: 'diff',
  test: 'test',
  log: 'build',
  review: 'lint',
}

export function EvidenceChain() {
  const t = useT()

  return (
    <Panel framed title={t('evidence.fig.contract')} className="kl-evi">
      <Stagger as="ul" childAs="li" className="kl-evi__cards">
        {EVIDENCE.map((a) => (
          <div key={a.kind} className={`kl-evi__card ${a.present ? '' : 'is-missing'}`}>
            <span className="kl-evi__kind k-mono">{KIND_LABEL[a.kind]}</span>
            {a.present ? (
              <>
                <span className="kl-evi__ref k-mono">{a.ref}</span>
                <Badge tone="ok">{t('ui.immutable')}</Badge>
              </>
            ) : (
              <>
                <span className="kl-evi__ref kl-evi__ref--missing">{t('evidence.fig.missing')}</span>
                {/*
                  A control, not a link: it is what the console offers here, and
                  what it offers is to *define the command* — never to skip the
                  requirement. Disabled because this page cannot do it for you.
                */}
                <button type="button" className="kl-evi__define" disabled>
                  {t('evidence.fig.define')}
                </button>
              </>
            )}
          </div>
        ))}
      </Stagger>

      <div className="kl-evi__gate">
        <span className="kl-evi__from k-mono">REVIEWING</span>
        <span className="kl-evi__arrow" aria-hidden="true">
          ─┼─▶
        </span>
        <Stamp className="kl-evi__locked">
          <Badge tone="err" mono>
            COMPLETED
          </Badge>
        </Stamp>
        <span className="kl-evi__lockword">{t('evidence.fig.locked')}</span>
      </div>
    </Panel>
  )
}
